import { useEffect, useRef, useCallback } from 'react';

/**
 * useInactivityLogout
 * Calls `onLogout` after `timeoutMs` milliseconds of no user activity.
 * Optionally calls `onWarn` at `warnMs` before logout as a warning.
 *
 * Activity events tracked: mousemove, mousedown, keydown, touchstart, scroll, click
 */
const useInactivityLogout = ({
    onLogout,
    onWarn,
    timeoutMs = 10 * 60 * 1000,   // 10 minutes
    warnMs = 60 * 1000,          // warn 1 minute before logout
}) => {
    const logoutTimer = useRef(null);
    const warnTimer = useRef(null);

    const clearTimers = useCallback(() => {
        if (logoutTimer.current) clearTimeout(logoutTimer.current);
        if (warnTimer.current) clearTimeout(warnTimer.current);
    }, []);

    const resetTimers = useCallback(() => {
        clearTimers();

        // Warn the user 1 minute before logout
        if (onWarn) {
            warnTimer.current = setTimeout(onWarn, timeoutMs - warnMs);
        }

        // Auto-logout after full timeout
        logoutTimer.current = setTimeout(() => {
            onLogout();
        }, timeoutMs);
    }, [clearTimers, onLogout, onWarn, timeoutMs, warnMs]);

    const lastActivity = useRef(Date.now());

    useEffect(() => {
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

        const handleActivity = () => {
            const now = Date.now();
            // Throttle: Only reset timers at most once per second (1000ms)
            if (now - lastActivity.current > 1000) {
                lastActivity.current = now;
                resetTimers();
            }
        };

        events.forEach(ev => window.addEventListener(ev, handleActivity, { passive: true }));
        resetTimers(); // start the timer immediately on mount

        return () => {
            events.forEach(ev => window.removeEventListener(ev, handleActivity));
            clearTimers();
        };
    }, [resetTimers, clearTimers]);

    return { clearTimers };
};

export default useInactivityLogout;
